import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, YouTubeChannel, YouTubeVideo, CreateYouTubeChannelRequest, UpdateYouTubeChannelRequest } from '@/lib/api'
import { Plus, Search, Edit, Trash2, X, RefreshCw, Play, Youtube, List, Shuffle, RotateCcw, Clock, Video, Eye, CheckCircle, Upload, AlertCircle, Key, CheckCircle2 } from 'lucide-react'

interface Category {
  id: string
  name: string
  type: string
}

interface ChannelFormData {
  name: string
  description: string
  youtube_url: string
  playback_mode: 'sequential' | 'shuffle' | 'loop'
  category_id: string
  auto_refresh: boolean
  refresh_interval_hours: number
}

const defaultFormData: ChannelFormData = {
  name: '',
  description: '',
  youtube_url: '',
  playback_mode: 'shuffle',
  category_id: '',
  auto_refresh: true,
  refresh_interval_hours: 24,
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

export default function YouTubeChannels() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingChannel, setEditingChannel] = useState<YouTubeChannel | null>(null)
  const [formData, setFormData] = useState<ChannelFormData>(defaultFormData)
  const [error, setError] = useState('')
  const [videosModalOpen, setVideosModalOpen] = useState(false)
  const [selectedChannel, setSelectedChannel] = useState<YouTubeChannel | null>(null)
  const [urlValidation, setUrlValidation] = useState<{ type?: string; id?: string } | null>(null)
  const [validatingUrl, setValidatingUrl] = useState(false)
  const [authExpanded, setAuthExpanded] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['youtube-channels', page, search],
    queryFn: () => api.getYouTubeChannels({ page, per_page: 20 }),
  })

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.getCategories({}),
    staleTime: 5 * 60 * 1000,
  })
  const categories: Category[] = categoriesData || []

  // YouTube Authentication Status
  const { data: authStatus, refetch: refetchAuth } = useQuery({
    queryKey: ['youtube-auth-status'],
    queryFn: async () => {
      const token = localStorage.getItem('access_token')
      if (!token) return { configured: false }
      const response = await fetch('/api/youtube/auth/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!response.ok) return { configured: false }
      return response.json()
    },
  })

  const { data: videosData, isLoading: videosLoading } = useQuery({
    queryKey: ['youtube-videos', selectedChannel?.id],
    queryFn: () => selectedChannel ? api.getYouTubeChannelVideos(selectedChannel.id, { per_page: 100 }) : null,
    enabled: !!selectedChannel,
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateYouTubeChannelRequest) => api.createYouTubeChannel(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['youtube-channels'] })
      closeModal()
    },
    onError: (err: Error) => setError(err.message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateYouTubeChannelRequest }) =>
      api.updateYouTubeChannel(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['youtube-channels'] })
      closeModal()
    },
    onError: (err: Error) => setError(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteYouTubeChannel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['youtube-channels'] })
    },
  })

  const refreshMutation = useMutation({
    mutationFn: (id: string) => api.refreshYouTubeChannel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['youtube-channels'] })
    },
  })

  const resetHistoryMutation = useMutation({
    mutationFn: (id: string) => api.resetYouTubeWatchHistory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['youtube-channels'] })
    },
  })

  const uploadCookiesMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('cookies', file)
      const response = await fetch('/api/youtube/auth/cookies', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
        body: formData,
      })
      if (!response.ok) {
        const text = await response.text()
        try {
          const data = JSON.parse(text)
          throw new Error(data.error || 'Upload failed')
        } catch {
          throw new Error(text || 'Upload failed')
        }
      }
      return response.json()
    },
    onSuccess: () => {
      setUploadStatus({ type: 'success', message: 'Cookies uploaded successfully!' })
      refetchAuth()
      setTimeout(() => setUploadStatus(null), 5000)
    },
    onError: (err: Error) => {
      setUploadStatus({ type: 'error', message: err.message })
    },
  })

  const testAuthMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/api/youtube/auth/test', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
      })
      const text = await response.text()
      try {
        return JSON.parse(text)
      } catch {
        throw new Error(text || 'Request failed')
      }
    },
    onSuccess: (data) => {
      if (data.success) {
        setUploadStatus({ type: 'success', message: `Authentication working! Test video: "${data.test_video?.title}"` })
      } else {
        setUploadStatus({ type: 'error', message: data.error || 'Authentication test failed' })
      }
      setTimeout(() => setUploadStatus(null), 8000)
    },
    onError: (err: Error) => {
      setUploadStatus({ type: 'error', message: err.message })
    },
  })

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadCookiesMutation.mutate(file)
    }
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingChannel(null)
    setFormData(defaultFormData)
    setError('')
    setUrlValidation(null)
  }

  const openCreate = () => {
    setEditingChannel(null)
    setFormData(defaultFormData)
    setModalOpen(true)
  }

  const openEdit = (channel: YouTubeChannel) => {
    setEditingChannel(channel)
    setFormData({
      name: channel.name,
      description: channel.description || '',
      youtube_url: channel.youtube_url,
      playback_mode: channel.playback_mode,
      category_id: channel.category_id || '',
      auto_refresh: channel.auto_refresh,
      refresh_interval_hours: channel.refresh_interval_hours,
    })
    setModalOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }
    if (!formData.youtube_url.trim()) {
      setError('YouTube URL is required')
      return
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      youtube_url: formData.youtube_url.trim(),
      playback_mode: formData.playback_mode,
      category_id: formData.category_id || undefined,
      auto_refresh: formData.auto_refresh,
      refresh_interval_hours: formData.refresh_interval_hours,
    }

    if (editingChannel) {
      updateMutation.mutate({ id: editingChannel.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const validateUrl = async (url: string) => {
    if (!url.trim()) {
      setUrlValidation(null)
      return
    }
    setValidatingUrl(true)
    try {
      const result = await api.parseYouTubeURL(url)
      setUrlValidation(result)
    } catch {
      setUrlValidation(null)
    } finally {
      setValidatingUrl(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.youtube_url) {
        validateUrl(formData.youtube_url)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [formData.youtube_url])

  const channels = data?.channels || []
  const total = data?.total || 0
  const perPage = 20
  const totalPages = Math.ceil(total / perPage)

  const filteredChannels = channels.filter(
    (ch) => !search || ch.name.toLowerCase().includes(search.toLowerCase())
  )

  const getPlaybackModeIcon = (mode: string) => {
    switch (mode) {
      case 'sequential':
        return <List className="w-4 h-4" />
      case 'shuffle':
        return <Shuffle className="w-4 h-4" />
      case 'loop':
        return <RotateCcw className="w-4 h-4" />
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">YouTube Channels</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 hidden sm:block">
            Create TV-like channels from YouTube playlists and channels
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shrink-0"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Add Channel</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* YouTube Authentication Status */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <button
          onClick={() => setAuthExpanded(!authExpanded)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <Key className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <span className="font-medium text-gray-900 dark:text-white">YouTube Authentication</span>
            {authStatus?.configured ? (
              <span className="flex items-center text-sm text-green-600 dark:text-green-400">
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Configured
                {authStatus.age_days > 0 && (
                  <span className="ml-2 text-gray-500 dark:text-gray-400">
                    ({authStatus.age_days} days old)
                  </span>
                )}
              </span>
            ) : (
              <span className="flex items-center text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4 mr-1" />
                Not Configured
              </span>
            )}
          </div>
          <span className="text-gray-400">{authExpanded ? '▲' : '▼'}</span>
        </button>

        {authExpanded && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            {/* Status Message */}
            {uploadStatus && (
              <div className={`mb-4 p-3 rounded-lg flex items-center space-x-2 ${
                uploadStatus.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
              }`}>
                {uploadStatus.type === 'success' ? (
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span>{uploadStatus.message}</span>
              </div>
            )}

            {/* Warning for old cookies */}
            {authStatus?.warning && (
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 rounded-lg flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{authStatus.warning}</span>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {/* Upload Section */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Upload Cookie File
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Export cookies from your browser using the "Get cookies.txt LOCALLY" extension,
                  then upload the file here.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadCookiesMutation.isPending}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Upload className="w-4 h-4" />
                  <span>{uploadCookiesMutation.isPending ? 'Uploading...' : 'Upload Cookies'}</span>
                </button>
              </div>

              {/* Test Section */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Test Authentication
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Verify that your cookies are working by testing against a YouTube video.
                </p>
                <button
                  onClick={() => testAuthMutation.mutate()}
                  disabled={!authStatus?.configured || testAuthMutation.isPending}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Play className="w-4 h-4" />
                  <span>{testAuthMutation.isPending ? 'Testing...' : 'Test Authentication'}</span>
                </button>
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                How to get cookies:
              </h4>
              <ol className="text-xs text-gray-500 dark:text-gray-400 list-decimal list-inside space-y-1">
                <li>Install browser extension: <a href="https://chromewebstore.google.com/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Get cookies.txt LOCALLY (Chrome)</a></li>
                <li>Go to <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">youtube.com</a> and make sure you're logged in</li>
                <li>Click the extension icon and export/download the cookies file</li>
                <li>Upload the file using the button above</li>
              </ol>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Note: Cookies typically last 1-2 months before needing refresh.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search channels..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Channels List */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
            Loading...
          </div>
        ) : filteredChannels.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
            No YouTube channels found. Click "Add Channel" to create one.
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredChannels.map((channel) => (
              <div key={channel.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                {/* Top row: thumbnail, name, actions */}
                <div className="flex items-start gap-3">
                  {channel.thumbnail_url ? (
                    <img
                      src={channel.thumbnail_url}
                      alt=""
                      className="w-10 h-10 rounded object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-red-100 dark:bg-red-900 flex items-center justify-center shrink-0">
                      <Youtube className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white truncate">{channel.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {channel.youtube_url}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => refreshMutation.mutate(channel.id)}
                      className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                      title="Refresh videos"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => resetHistoryMutation.mutate(channel.id)}
                      className="p-2 text-gray-400 hover:text-orange-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 hidden sm:block"
                      title="Reset watch history"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEdit(channel)}
                      className="p-2 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${channel.name}"?`)) {
                          deleteMutation.mutate(channel.id)
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Bottom row: metadata badges */}
                <div className="mt-2 ml-[52px] flex flex-wrap items-center gap-2 text-sm">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      channel.youtube_type === 'playlist'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                    }`}
                  >
                    {channel.youtube_type === 'playlist' ? 'Playlist' : 'Channel'}
                  </span>
                  <span className="flex items-center text-gray-500 dark:text-gray-400 text-xs">
                    {getPlaybackModeIcon(channel.playback_mode)}
                    <span className="ml-1 capitalize">{channel.playback_mode}</span>
                  </span>
                  <button
                    onClick={() => {
                      setSelectedChannel(channel)
                      setVideosModalOpen(true)
                    }}
                    className="text-blue-600 hover:text-blue-800 flex items-center text-xs"
                  >
                    <Video className="w-3 h-3 mr-1" />
                    {channel.video_count} videos
                  </button>
                  <span className="flex items-center text-gray-500 dark:text-gray-400 text-xs">
                    <Clock className="w-3 h-3 mr-1" />
                    {formatDuration(channel.total_duration_seconds)}
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      channel.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {channel.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing <span className="font-medium">{(page - 1) * perPage + 1}</span> to{' '}
              <span className="font-medium">{Math.min(page * perPage, total)}</span> of{' '}
              <span className="font-medium">{total}</span> results
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingChannel ? 'Edit YouTube Channel' : 'Add YouTube Channel'}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">{error}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="My YouTube Channel"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  YouTube URL *
                </label>
                <input
                  type="text"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://www.youtube.com/@channel or playlist URL"
                />
                {validatingUrl && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Validating URL...</p>
                )}
                {urlValidation && (
                  <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                    Detected: {urlValidation.type} ({urlValidation.id})
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={2}
                  placeholder="Optional description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Playback Mode
                </label>
                <select
                  value={formData.playback_mode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      playback_mode: e.target.value as 'sequential' | 'shuffle' | 'loop',
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="shuffle">Shuffle (Smart - no repeats until all watched)</option>
                  <option value="sequential">Sequential (Play in order)</option>
                  <option value="loop">Loop (Continuous playback)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">-- Select Category --</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="auto_refresh"
                    checked={formData.auto_refresh}
                    onChange={(e) =>
                      setFormData({ ...formData, auto_refresh: e.target.checked })
                    }
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <label htmlFor="auto_refresh" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Auto-refresh videos
                  </label>
                </div>
              </div>

              {formData.auto_refresh && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Refresh Interval (hours)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={168}
                    value={formData.refresh_interval_hours}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        refresh_interval_hours: parseInt(e.target.value) || 24,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingChannel
                    ? 'Save Changes'
                    : 'Create Channel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Videos Modal */}
      {videosModalOpen && selectedChannel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedChannel.name}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedChannel.video_count} videos &bull;{' '}
                  {formatDuration(selectedChannel.total_duration_seconds)} total
                </p>
              </div>
              <button
                onClick={() => {
                  setVideosModalOpen(false)
                  setSelectedChannel(null)
                }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {videosLoading ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading videos...</div>
              ) : (
                <div className="space-y-2">
                  {videosData?.videos.map((video: YouTubeVideo, index: number) => (
                    <div
                      key={video.id}
                      className="flex items-center p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-100 dark:border-gray-700"
                    >
                      <div className="text-gray-400 w-8 text-center">{index + 1}</div>
                      {video.thumbnail_url ? (
                        <img
                          src={video.thumbnail_url}
                          alt=""
                          className="w-24 h-14 rounded object-cover mr-3"
                        />
                      ) : (
                        <div className="w-24 h-14 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center mr-3">
                          <Play className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white truncate">{video.title}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center space-x-3">
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatDuration(video.duration_seconds)}
                          </span>
                          {video.view_count && (
                            <span className="flex items-center">
                              <Eye className="w-3 h-3 mr-1" />
                              {video.view_count.toLocaleString()} views
                            </span>
                          )}
                        </div>
                      </div>
                      {video.is_available ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <span className="text-xs text-red-500">Unavailable</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
